import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'
import { Component, createRef, useEffect, useState } from 'react'

import { ABO_LABEL } from 'shared/consts'
import store, { subscribe } from 'shared/store'
import {
  PRODUCT_FILTER_CHANGED,
  PRODUCT_FILTER_RESET,
} from 'shared/store/ducks/events'
import { upperCaseFirst } from 'shared/utils/stringUtils'
import Button from 'views/components/atoms/Button'
import IconCaretRight from 'views/components/atoms/Icon/IconCaretRight'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import InputCheckbox from 'views/components/molecules/InputCheckbox'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import RefinementItem from 'views/components/organisms/AlgoliaInstantSearch/Algolia/RefinementItem'
import ProductFilterModal from 'views/components/organisms/AlgoliaInstantSearch/ProductFilter/ProductFilterModal'
import Collapsible from 'views/components/organisms/Collapsible'

import { closeComponentForModal } from './helpers'

import './RefinementList.scss'

// eslint-disable-next-line react/prop-types
const ModalButton = ({ id, title, count }) => (
  <div className="o-RefinementList--modal-button u-hidden@medium">
    <ModalShowButton component={ButtonAsLink} name={id} modalId={id} neutral>
      <div className="l-flex">
        <p className="u-no-margin u-color--dark l-flex__primary u-text-align--left u-font-weight--bold o-RefinementList--modal-button-text">
          {title}{' '}
          {count ? (
            <span className="o-RefinementList--modal-button-counter">
              ({count})
            </span>
          ) : null}
        </p>
        <IconCaretRight />
      </div>
    </ModalShowButton>
  </div>
)

// eslint-disable-next-line react/prop-types
const ListItem = ({ item, refine, attribute, searchStateRefinementList }) => {
  const { label, count, alternativeLabel } = item
  const displayedLabel = alternativeLabel || label
  const componentId = `refinement-control-${attribute}-${displayedLabel}`
  const [isChecked, setIsChecked] = useState(
    // eslint-disable-next-line react/prop-types
    Boolean(searchStateRefinementList?.[attribute]?.includes?.(label))
  )
  useEffect(() => {
    return subscribe.after(PRODUCT_FILTER_RESET, () => {
      setIsChecked(false)
    })
  })
  return (
    <li
      key={displayedLabel}
      className="u-no-border-radius u-border--bottom@small-tiny u-padding@small-tiny u-margin-x-small--bottom@medium o-AlgoliaInstantSearch__RefinementList--list-item"
    >
      <InputCheckbox
        id={componentId}
        name={componentId}
        checked={isChecked}
        onChange={() => {
          setIsChecked(!isChecked)
          store.dispatch({
            type: PRODUCT_FILTER_CHANGED,
            attribute,
            selected: label,
            refine,
            setIsChecked,
          })
        }}
        disableAutoCompleteAndFriends
        labelSize="small"
      >
        {upperCaseFirst(displayedLabel)} ({count})
      </InputCheckbox>
    </li>
  )
}

ListItem.propTypes = {
  item: shape({}).isRequired,
}

class RefinementList extends Component {
  constructor(props) {
    super(props)
    this.container = createRef()

    this.state = this.getComponentState()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      this.setState(this.getComponentState())
    }
  }

  handleNextItemsState = itemsToAdd => {
    const { visibleItems, nextItems } = this.state
    this.setState({
      visibleItems: [...visibleItems, ...nextItems],
      nextItems: itemsToAdd,
    })
  }

  getComponentState = () => {
    const {
      items: itemsRaw,
      maxVisible,
      maxCollapsed,
      shippingOptionsData,
    } = this.props

    const items = itemsRaw.filter(item => item.label !== ABO_LABEL)

    const { visibleItemsCount: visibleItemsCountFromState = maxVisible } =
      this.state || {}

    const refinedItemsCount = items.filter(item => item.isRefined).length

    const currentMaxVisible = Math.max(
      visibleItemsCountFromState,
      maxVisible,
      refinedItemsCount
    )

    let visibleItems = items.slice(0, currentMaxVisible)
    const visibleItemsCount = visibleItems.length
    const showMoreCount = Math.min(
      items.length - visibleItemsCount,
      maxCollapsed
    )

    const nextItems = []

    const hasShippingOption = Boolean(shippingOptionsData?.length)

    if (hasShippingOption) {
      visibleItems = shippingOptionsData
    }

    return { items, visibleItems, nextItems, visibleItemsCount, showMoreCount }
  }

  handleShowMoreButton = () => {
    const { maxCollapsed } = this.props
    const { items } = this.state

    const { visibleItemsCount, showMoreCount } = this.state
    const newVisibleCount = visibleItemsCount + maxCollapsed

    const newShowMoreCount = Math.min(
      items.length - newVisibleCount,
      maxCollapsed
    )

    const nextItems = items.slice(
      visibleItemsCount,
      visibleItemsCount + showMoreCount
    )

    this.setState(
      {
        showMoreCount: newShowMoreCount,
        visibleItemsCount: newVisibleCount,
      },
      () => {
        this.handleNextItemsState(nextItems)
      }
    )

    // Merge the nextItems above into the visibleItems and clear the
    // variable for the transition effect.
    setTimeout(() => {
      this.handleNextItemsState([])
    }, 300)
  }

  render() {
    const {
      refine,
      attribute,
      label,
      isHidden,
      shippingOptionsData,
      searchStateRefinementList,
      className,
      currentRefinement,
    } = this.props

    const { items, visibleItems, showMoreCount, nextItems } = this.state

    const id = `refinement-control-${attribute}`
    const modalId = `${id}-modal`

    const hasShippingOption = Boolean(shippingOptionsData?.length)
    const hasFilters = Boolean(items.length)
    const shouldRenderFilters = (hasFilters || hasShippingOption) && !isHidden
    const divClass = classNames(
      'u-no-border-radius u-border--bottom u-padding@small-tiny',
      {
        'u-hidden': !shouldRenderFilters,
      },
      className
    )
    const nextItemsClass = classNames('o-RefinementList__nextItem', {
      'o-RefinementList__nextItem--enterActive': nextItems.length,
    })
    const labelText = `${label}${hasShippingOption ? '' : ` (${items.length})`}`

    let renderItem

    if (hasShippingOption) {
      renderItem = item => <RefinementItem key={item.id} {...item} />
    } else {
      renderItem = item => (
        <ListItem
          key={item.value}
          {...{
            searchStateRefinementList,
            item,
            attribute,
            refine,
          }}
        />
      )
    }

    return (
      <div className={divClass}>
        <ModalButton
          {...{
            id: modalId,
            title: label,
            count: (currentRefinement || []).length,
          }}
        />
        <div
          className="o-RefinementList"
          data-clientside-hook="refinementList"
          data-list-attribute={attribute}
        >
          <Collapsible
            id={id}
            label={labelText}
            expanded
            inFilter
            headingLevel="p"
            forceDisplayWhenCollapsed
          >
            <ProductFilterModal
              id={modalId}
              title={labelText}
              closeComponent={closeComponentForModal()}
            >
              <div className="o-RefinementList__items__wrapper u-padding--sides@small-tiny">
                {/* Top hits */}
                <ul className="a-list-reset">
                  {visibleItems.map(renderItem)}

                  <li className={nextItemsClass}>
                    {nextItems.map(renderItem)}
                  </li>
                </ul>

                <div
                  className={classNames(
                    'o-RefinementList--expandable-container',
                    {
                      'u-hidden': showMoreCount <= 0,
                    }
                  )}
                  ref={this.container}
                >
                  <div className="u-padding--top u-padding--bottom@small-tiny o-AlgoliaInstantSearch__RefinementList--expand-link">
                    <ButtonAsLink
                      name={`expandable-link-${attribute}-expand`}
                      onClick={this.handleShowMoreButton}
                    >
                      <Trans
                        id="general.label.showNMore"
                        values={{
                          count: showMoreCount,
                        }}
                      />
                    </ButtonAsLink>
                  </div>
                </div>
                <Button
                  name={`refinement-list-close-modal-${attribute}`}
                  className="o-RefinementList__cta u-hidden@medium"
                  data-clientside-hook="refinementList__close-modal"
                  isClientsideControlled
                  ariaHidden="true"
                >
                  <Trans id="general.link.label.back" />
                </Button>
                <Button
                  name={`refinement-list-confirm-selection-${attribute}`}
                  className="o-RefinementList__cta u-hidden@medium"
                  isClientsideControlled
                  data-clientside-hook="refinementList__confirm-selection"
                  ariaHidden="true"
                >
                  <Trans id="general.label.link.apply" />
                </Button>
              </div>
            </ProductFilterModal>
          </Collapsible>
        </div>
      </div>
    )
  }
}

RefinementList.propTypes = {
  label: string.isRequired,
  attribute: string.isRequired,
  items: arrayOf(shape({})).isRequired,
  refine: func.isRequired,
  searchStateRefinementList: shape({}),
  isHidden: bool,
  shippingOptionsData: arrayOf(shape({})),
  maxVisible: number,
  maxCollapsed: number,
  className: string,
  currentRefinement: arrayOf(string),
}

RefinementList.defaultProps = {
  isHidden: false,
  searchStateRefinementList: null,
  shippingOptionsData: [],
  maxVisible: 5,
  maxCollapsed: 10,
}

export default RefinementList
